import { useEffect, useState } from 'react';

import { Box, Divider, Grid, Paper, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import { isEmptyOrUndefined } from '@zippeditoolsjs/blocks';

import CircularLoader from "../../../tools/CircularLoader";
import CloseButton from '../../../tools/CloseButton';

export default function SummaryCard(props) {
  const {
    t,
    storeMap,
    setIsSidenavOpen,
    sidenavRef,
    navigationReportSummary,
    isLoadingNavigationReportSummary,
    currentSessionObstaclesInfo,
    setCurrentSessionObstaclesInfo,
  } = props;
  const [currentTab, setCurrentTab] = useState(currentSessionObstaclesInfo?.sessionName);

  useEffect(() => {
    setCurrentTab(currentSessionObstaclesInfo.sessionName);
  }, [currentSessionObstaclesInfo.sessionName])

  const handleChange = (_, newValue) => {
    setCurrentTab(newValue);
    setCurrentSessionObstaclesInfo({
      ...storeMap.map_info.obstacles_by_session[newValue],
      sessionName: newValue,
    })
  };

  return (
    <Paper ref={sidenavRef} sx={{
      width: { xs: '100%', md: '35%' },
      height: 'auto',
      ml: { xs: 0, md: 1 },
      mt: { xs: 1, md: 0 },
      overflow: 'hidden', // The inner Box will manage scroll.
      display: 'flex',
      flexDirection: 'column'
    }}>
      {isEmptyOrUndefined(navigationReportSummary, 'object') ?
        <Grid container justifyContent='center' alignItems='center' sx={{ height: '100%' }}>
          <Typography>{t('cws_app.general.No_data', 'No data')}</Typography>
        </Grid>
        :
        <Box sx={{ flexGrow: 1, display: 'flex', height: '100%' }}>
          <Tabs
            orientation="vertical"
            value={currentTab}
            onChange={handleChange}
            aria-label="Vertical session tabs"
            sx={{
              minWidth: '30px', // Need same pixels as the minWidth from .MuiTab-root
              borderRight: 1,
              borderColor: 'divider',
              '& .MuiTab-root': {
                minWidth: '30px',
                padding: 0,
                alignItems: 'center',
              },
            }}
          >
            {Object.keys(navigationReportSummary).map((key, index) => (
              <Tab key={key} label={index + 1} value={key} />
            ))}
          </Tabs>

          {/* Tab panels container */}
          {Object.entries(navigationReportSummary).map(([sessionName, navigationData]) => (
            <Box
              key={sessionName}
              role="tabpanel"
              hidden={currentTab !== sessionName}
              sx={{
                flexGrow: 1, // This Box takes all remaining space horizontally
                overflowY: 'auto', // Enable vertical scrolling for content if it overflows
                px: 1,
                position: 'relative', // Set relative for absolute positioning of children like CloseButton
              }}
            >
              {/* Header */}
              <Grid container item justifyContent='flex-end' alignItems='center' sx={{ position: 'absolute', top: 0, right: 0, zIndex: 1 }}>
                <CloseButton setIsOpen={setIsSidenavOpen} isColorbgAlwaysDark={false} />
              </Grid>
              {isLoadingNavigationReportSummary ?
                <Grid container justifyContent='center' alignItems='center' sx={{ height: '100%' }}>
                  <CircularLoader typographyVariant='body' message={t('cws_app.digital_store.loading_navigation_summary', 'Loading navigation summary')} />
                </Grid>
                :
                <Grid container>
                  {/* Session title */}
                  <Grid container item justifyContent='flex-start' mt={4}>
                    <Typography variant='h5'>{t('cws_app.digital_store.session', 'Session')} {sessionName.split('-')[0].trim()}</Typography>
                  </Grid>
                  {/* Obstacles and general information */}
                  <Grid container item direction='column' justifyContent='center' alignItems='center' my={1}>
                    {/* Planned navigation start and arrival time */}
                    <Grid container alignItems='flex-start'>
                      <Grid container item xs={6}>
                        <Typography variant='body2' color="textSecondary">{t('cws_app.digital_store.planned_start_time', 'Planned start time')}</Typography>
                      </Grid>
                      <Grid container item xs={6}>
                        <Typography>{navigationData?.session_info?.planned_for ? navigationData?.session_info?.planned_for : '-'}</Typography>
                      </Grid>
                      <Grid container item xs={6}>
                        <Typography variant='body2' color="textSecondary">{t('cws_app.digital_store.planned_arrival_time', 'Planned arrival time')}</Typography>
                      </Grid>
                      <Grid container item xs={6}>
                        <Typography>{navigationData?.session_info?.planned_arrival ? navigationData?.session_info?.planned_arrival : '-'}</Typography>
                      </Grid>
                    </Grid>
                    <Divider sx={{ width: '100%', my: 1 }} />
                    {/* Day and permanent obstacles */}
                    <Grid container item spacing={2}>
                      <Grid container item xs={6} flexDirection='column'>
                        <Typography variant='subtitle2'>{t('cws_app.digital_store.day_obstacles', 'Day obstacles')}</Typography>
                        <Divider sx={{ backgroundColor: '#fd6303', height: '2px', mt: 0.75, mb: 0.5 }} />
                        <Typography variant='h4' sx={{ color: '#fd6303' }}>{currentSessionObstaclesInfo.day_obstacles}</Typography>
                      </Grid>
                      <Grid container item xs={6} flexDirection='column'>
                        <Typography variant='subtitle2'>{t('cws_app.digital_store.constant_obstacles', 'Constant obstacles')}</Typography>
                        <Divider sx={{ backgroundColor: '#9d03fd', height: '2px', mt: 0.75, mb: 0.5 }} />
                        <Typography variant='h4' sx={{ color: '#9d03fd' }}>{currentSessionObstaclesInfo.permanent_obstacles}</Typography>
                      </Grid>
                    </Grid>
                    {/* Robot states */}
                    <Grid container item alignItems='flex-start' flexDirection='column' mt={1}>
                      <Grid container item alignItems='center'>
                        <Divider sx={{ borderStyle: 'dotted', borderColor: '#00ff00', borderWidth: '4px', width: '20%', borderBottom: '0px', mt: 0.75, mb: 0.5, mr: 1.25 }} />
                        <Typography variant='body2' color="textSecondary">{t('cws_app.digital_store.navigation_mode', 'Navigation mode')}</Typography>
                      </Grid>
                      <Grid container item alignItems='center'>
                        <Divider sx={{ borderStyle: 'dotted', borderColor: '#0000ff', borderWidth: '4px', width: '20%', borderBottom: '0px', mt: 0.75, mb: 0.5, mr: 1.25 }} />
                        <Typography variant='body2' color="textSecondary">{t('cws_app.digital_store.data_capture_mode', 'Data capture mode')}</Typography>
                      </Grid>
                      <Grid container item alignItems='center'>
                        <Divider sx={{ borderStyle: 'dotted', borderColor: '#ff0000', borderWidth: '4px', width: '20%', borderBottom: '0px', mt: 0.75, mb: 0.5, mr: 1.25 }} />
                        <Typography variant='body2' color="textSecondary">{t('cws_app.digital_store.robot_disoriented', 'Robot disoriented')}</Typography>
                      </Grid>
                    </Grid>
                    <Divider sx={{ width: '100%', my: 1 }} /> {/* Simplified Divider styling */}
                    {/* General information */}
                    <Grid container alignItems='flex-start'>
                      <Typography variant='subtitle2'>{t('cws_app.digital_store.general_information', 'General information')}</Typography>
                    </Grid>
                    <Grid container alignItems='flex-start'>
                      <Grid container item xs={6}>
                        <Typography variant='body2' color="textSecondary">{t('cws_app.digital_store.actual_start_time', 'Actual start time')}</Typography>
                      </Grid>
                      <Grid container item xs={6}>
                        <Typography>{navigationData?.session_info?.nav_start ? navigationData?.session_info?.nav_start : '-'}</Typography>
                      </Grid>
                      <Grid container item xs={6}>
                        <Typography variant='body2' color="textSecondary">{t('cws_app.digital_store.actual_arrival_time', 'Actual arrival time')}</Typography>
                      </Grid>
                      <Grid container item xs={6}>
                        <Typography>{navigationData?.session_info?.nav_finish ? navigationData?.session_info?.nav_finish : '-'}</Typography>
                      </Grid>
                      <Grid container item xs={6}>
                        <Typography variant='body2' color="textSecondary">{t('cws_app.digital_store.total_navigation_time', 'Total navigation time')}</Typography>
                      </Grid>
                      <Grid container item xs={6}>
                        <Typography>{navigationData?.session_info?.nav_time ? navigationData?.session_info?.nav_time : '-'}</Typography>
                      </Grid>
                      <Grid container item xs={6}>
                        <Typography variant='body2' color="textSecondary">{t('cws_app.digital_store.planned_linears', 'Planned linears')}</Typography>
                      </Grid>
                      <Grid container item xs={6}>
                        <Typography>{navigationData?.aisles_info?.linears_count ? navigationData?.aisles_info?.linears_count : '-'}</Typography>
                      </Grid>
                      <Grid container item xs={6}>
                        <Typography variant='body2' color="textSecondary">{t('cws_app.digital_store.navigated_linears', 'Navigated linears')}</Typography>
                      </Grid>
                      <Grid container item xs={6}>
                        <Typography>{navigationData?.aisles_info?.navigated_linears_count ? navigationData?.aisles_info?.navigated_linears_count : '-'}</Typography>
                      </Grid>
                      <Grid container item xs={6} alignItems='center'>
                        <Tooltip title={t('cws_app.digital_store.planned_vs_navigated', 'Planned linears vs navigated linears')} arrow>
                          <Typography variant='body2' color="textSecondary">{t('cws_app.digital_store.compliance', 'Compliance')}</Typography>
                        </Tooltip>
                      </Grid>
                      <Grid container item xs={6}>
                        <Typography>{navigationData?.aisles_info?.coverage ? `${navigationData?.aisles_info?.coverage}%` : '-'}</Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              }
            </Box>
          ))}
        </Box>
      }
    </Paper>
  )
}